import { trpc } from '@/trpcClient';
import type { FlowSensorDashboardDto } from '@shelflix/trpc/routes/flowSensor/dashboard/useCase';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@shelflix/ui-web/components/ui/chart';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, XAxis, YAxis } from 'recharts';
import { toPeriodInput, type FlowSensorFilterValue } from './FlowSensorFilter';
import { WEEKDAYS, formatDateTime, formatDay, formatDuration, formatNumber } from './format';

const dayConfig = {
  approaches: { label: 'Aproximações recebidas', color: 'var(--primary)' },
  sensorReported: { label: 'Contadas pelo sensor', color: 'var(--gray-secondary)' },
} satisfies ChartConfig;

const hourConfig = { approaches: { label: 'Aproximações', color: 'var(--primary)' } } satisfies ChartConfig;

function Stat({ title, value, hint }: { title: string; value: string; hint?: string }) {
  return (
    <Card className="gap-1 py-4">
      <CardHeader className="px-5">
        <CardTitle className="text-sm font-medium text-[var(--gray-secondary)]">{title}</CardTitle>
      </CardHeader>
      <CardContent className="px-5">
        <p className="text-3xl font-semibold text-[var(--gray-primary)]">{value}</p>
        {hint && <p className="mt-1 text-xs text-[var(--gray-secondary)]">{hint}</p>}
      </CardContent>
    </Card>
  );
}

// Dia da semana × hora: um tom só, mais forte = mais aproximações. Hover mostra o valor.
function Heatmap({ data }: { data: FlowSensorDashboardDto['heatmap'] }) {
  const max = Math.max(1, ...data.map((d) => d.approaches));
  const value = (weekday: number, hour: number) =>
    data.find((d) => d.weekday === weekday && d.hour === hour)?.approaches ?? 0;
  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[640px] gap-[2px]" style={{ gridTemplateColumns: '40px repeat(24, minmax(0, 1fr))' }}>
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="text-center text-[10px] text-[var(--gray-secondary)]">
            {h % 3 === 0 ? `${h}h` : ''}
          </div>
        ))}
        {WEEKDAYS.map((label, i) => (
          <div key={label} className="contents">
            <div className="pr-1 text-right text-xs leading-6 text-[var(--gray-secondary)]">{label}</div>
            {Array.from({ length: 24 }, (_, h) => {
              const v = value(i + 1, h);
              return (
                <div
                  key={h}
                  title={`${label} ${h}h–${h + 1}h: ${formatNumber(v)} aproximação(ões)`}
                  className="h-6 rounded-[3px]"
                  style={{
                    background: v ? 'var(--primary)' : 'var(--secondary)',
                    opacity: v ? 0.15 + 0.85 * (v / max) : 1,
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export function DashboardTab({ filter }: { filter: FlowSensorFilterValue }) {
  const { data, isLoading, isError } = useQuery({
    ...trpc.flowSensor.dashboard.queryOptions(toPeriodInput(filter)),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });

  if (isLoading) return <p className="p-4">Carregando dashboard...</p>;
  if (isError || !data) return <p className="p-4 text-red-500">Erro ao carregar o dashboard.</p>;

  const days = data.byDay.length;
  const peakHour = data.byHour.reduce((a, b) => (b.approaches > a.approaches ? b : a), data.byHour[0]);
  const reportedTotal = data.byDay.reduce((sum, d) => sum + (d.sensorReported ?? 0), 0);
  const hasReported = data.byDay.some((d) => d.sensorReported !== null);
  const dayData = data.byDay.map((d) => ({ ...d, label: formatDay(d.day) }));
  const hourData = data.byHour.map((h) => ({ ...h, label: `${h.hour}h` }));

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          title="Aproximações no período"
          value={formatNumber(data.totals.approaches)}
          hint={hasReported ? `Contadas pelos sensores: ${formatNumber(reportedTotal)}` : undefined}
        />
        <Stat
          title="Média por dia"
          value={formatNumber(Math.round((data.totals.approaches / Math.max(1, days)) * 10) / 10)}
          hint={`${days} dia(s)`}
        />
        <Stat
          title="Permanência média"
          value={formatDuration(data.totals.avgDurationS)}
          hint={`Total: ${formatDuration(data.totals.totalDurationS)}`}
        />
        <Stat
          title="Sensores online"
          value={`${data.sensors.online} de ${data.sensors.total}`}
          hint={peakHour.approaches ? `Horário de pico: ${peakHour.hour}h–${peakHour.hour + 1}h` : undefined}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Aproximações por dia</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={dayConfig} className="h-[280px] w-full">
            <ComposedChart data={dayData} margin={{ top: 8, left: 0, right: 8 }}>
              <CartesianGrid vertical={false} strokeOpacity={0.2} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} minTickGap={16} />
              <YAxis tickLine={false} axisLine={false} allowDecimals={false} width={40} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="approaches" fill="var(--color-approaches)" radius={[4, 4, 0, 0]} maxBarSize={36} />
              {hasReported && (
                <Line
                  dataKey="sensorReported"
                  stroke="var(--color-sensorReported)"
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  dot={false}
                  connectNulls
                />
              )}
              {hasReported && <Legend verticalAlign="top" height={28} />}
            </ComposedChart>
          </ChartContainer>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Aproximações por hora do dia</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={hourConfig} className="h-[240px] w-full">
              <ComposedChart data={hourData} margin={{ top: 8, left: 0, right: 8 }}>
                <CartesianGrid vertical={false} strokeOpacity={0.2} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} interval={2} />
                <YAxis tickLine={false} axisLine={false} allowDecimals={false} width={40} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="approaches" fill="var(--color-approaches)" radius={[4, 4, 0, 0]} />
              </ComposedChart>
            </ChartContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Dia da semana × hora</CardTitle>
          </CardHeader>
          <CardContent>
            <Heatmap data={data.heatmap} />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Por sensor</CardTitle>
          </CardHeader>
          <CardContent>
            {data.bySensor.length === 0 ? (
              <p className="text-sm text-[var(--gray-secondary)]">Nenhuma aproximação no período.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-[var(--gray-secondary)]">
                  <tr>
                    <th className="py-2 font-medium">Sensor</th>
                    <th className="py-2 text-right font-medium">Aproximações</th>
                    <th className="py-2 text-right font-medium">Permanência média</th>
                    <th className="py-2 text-right font-medium">Última</th>
                  </tr>
                </thead>
                <tbody>
                  {data.bySensor.map((s) => (
                    <tr key={s.uuid} className="border-t">
                      <td className="py-2">{s.name}</td>
                      <td className="py-2 text-right font-medium">{formatNumber(s.approaches)}</td>
                      <td className="py-2 text-right">{formatDuration(s.avgDurationS)}</td>
                      <td className="py-2 text-right">{formatDateTime(s.lastEventAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Conferência com o sensor</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            <p className="text-[var(--gray-secondary)]">
              Cada sensor publica o total do dia. Diferença em dia em andamento costuma ser a fila do sensor ainda
              enviando; em dia fechado, indica eventos perdidos.
            </p>
            {data.mismatches.length === 0 ? (
              <p className="font-medium">Tudo confere no período.</p>
            ) : (
              <table className="w-full">
                <thead className="text-left text-[var(--gray-secondary)]">
                  <tr>
                    <th className="py-2 font-medium">Sensor</th>
                    <th className="py-2 font-medium">Dia</th>
                    <th className="py-2 text-right font-medium">Recebidas</th>
                    <th className="py-2 text-right font-medium">No sensor</th>
                  </tr>
                </thead>
                <tbody>
                  {data.mismatches.slice(0, 15).map((m) => (
                    <tr key={`${m.sensorName}-${m.day}`} className="border-t">
                      <td className="py-2">{m.sensorName}</td>
                      <td className="py-2">
                        {formatDay(m.day)} {m.final ? '' : '(em andamento)'}
                      </td>
                      <td className="py-2 text-right">{formatNumber(m.received)}</td>
                      <td className="py-2 text-right">{formatNumber(m.sensorReported)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
